import { formatDisplayDate } from '../types';

type BriefingStatusStateProps = {
  status: 'insufficient' | 'failed' | 'missing';
  issueDate: string;
  latestPublishedDate: string | null;
  previousPublishedDate: string | null;
  nextPublishedDate: string | null;
  storyCount?: number;
  failureReason?: string | null;
};

export function BriefingStatusState({
  status,
  latestPublishedDate,
  previousPublishedDate,
  nextPublishedDate,
}: BriefingStatusStateProps) {
  const nearestDate =
    nextPublishedDate ?? previousPublishedDate ?? latestPublishedDate;

  if (status === 'failed') {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center px-4">
        <div className="w-12 h-12 rounded-full bg-warning-100 dark:bg-warning-900/30 flex items-center justify-center mb-5">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-6 h-6 text-warning-600 dark:text-warning-400"
          >
            <path
              fillRule="evenodd"
              d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-neutral-800 dark:text-neutral-100 mb-2">
          We hit a snag
        </h2>
        <p className="text-neutral-500 dark:text-neutral-400 mb-6 max-w-sm">
          Today&apos;s briefing couldn&apos;t be generated. We&apos;re on it.
        </p>
        {nearestDate && (
          <a
            href={`/today?date=${nearestDate}`}
            className="px-5 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            Read {nearestDate === latestPublishedDate ? 'latest' : 'nearest'}{' '}
            briefing
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center text-center px-4 py-10">
      {nearestDate ? (
        <>
          <p className="text-sm font-medium text-neutral-400 dark:text-neutral-500 mb-1 tracking-wide uppercase">
            No briefing on this date
          </p>
          <h2 className="text-xl font-semibold text-neutral-800 dark:text-neutral-100 mb-2">
            Check out a recent edition instead
          </h2>
          <p className="text-neutral-500 dark:text-neutral-400 mb-8 max-w-sm">
            Briefings aren&apos;t published every day, but there&apos;s always
            good news waiting.
          </p>

          <a
            href={`/today?date=${nearestDate}`}
            className="group flex items-center gap-4 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-6 py-5 hover:border-primary/40 dark:hover:border-primary/40 transition-colors max-w-md w-full text-left"
          >
            <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-5 h-5 text-primary"
              >
                <path
                  fillRule="evenodd"
                  d="M2 3.5A1.5 1.5 0 013.5 2h9A1.5 1.5 0 0114 3.5v11.75A2.75 2.75 0 0016.75 18h-12A2.75 2.75 0 012 15.25V3.5zm3.75 7a.75.75 0 000 1.5h4.5a.75.75 0 000-1.5h-4.5zm0 3a.75.75 0 000 1.5h4.5a.75.75 0 000-1.5h-4.5zM5 5.75A.75.75 0 015.75 5h4.5a.75.75 0 01.75.75v2.5a.75.75 0 01-.75.75h-4.5A.75.75 0 015 8.25v-2.5z"
                  clipRule="evenodd"
                />
                <path d="M16.5 6.5h-1v8.75a1.25 1.25 0 102.5 0V8a1.5 1.5 0 00-1.5-1.5z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-neutral-900 dark:text-white group-hover:text-primary transition-colors">
                {formatDisplayDate(nearestDate)}
              </div>
              <div className="text-xs text-neutral-400 dark:text-neutral-500">
                5 uplifting stories
              </div>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-5 h-5 text-neutral-300 dark:text-neutral-600 group-hover:text-primary transition-colors flex-shrink-0"
            >
              <path
                fillRule="evenodd"
                d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clipRule="evenodd"
              />
            </svg>
          </a>

          <a
            href="/articles"
            className="mt-4 text-sm text-neutral-400 dark:text-neutral-500 hover:text-primary transition-colors"
          >
            or browse all articles
          </a>
        </>
      ) : (
        <>
          <h2 className="text-xl font-semibold text-neutral-800 dark:text-neutral-100 mb-2">
            Briefings are on their way
          </h2>
          <p className="text-neutral-500 dark:text-neutral-400 mb-6 max-w-sm">
            We&apos;re still building the archive. Check back soon or explore
            positive news now.
          </p>
          <a
            href="/articles"
            className="px-5 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            Browse articles
          </a>
        </>
      )}
    </div>
  );
}
